import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import {
  IndianRupee, TrendingUp, CreditCard, Users,
  ChevronLeft, Search, Eye, Download, ArrowUpRight,
  DollarSign,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import Pagination from '../../components/ui/Pagination';
import Modal from '../../components/ui/Modal';
import api from '../../services/api';

const AdminBillingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRecurringRevenue: 0,
    activeSubscriptions: 0,
    totalOrganizations: 0,
    avgRevenuePerOrg: 0,
    totalSubscriptions: 0,
  });
  const [subscriptionsByPlan, setSubscriptionsByPlan] = useState({});
  const [subscriptionsByStatus, setSubscriptionsByStatus] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [subscriberTrends, setSubscriberTrends] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState({ total: 0, newThisWeek: 0, growthPercent: 0 });

  // Transaction filters
  const [txSearchTerm, setTxSearchTerm] = useState('');
  const [txFilterPlan, setTxFilterPlan] = useState('all');
  const [txPage, setTxPage] = useState(1);
  const [txPagination, setTxPagination] = useState({ total: 0, pages: 1 });

  // Subscription filters
  const [subscriptions, setSubscriptions] = useState([]);
  const [subSearchTerm, setSubSearchTerm] = useState('');
  const [subFilterPlan, setSubFilterPlan] = useState('all');
  const [subFilterStatus, setSubFilterStatus] = useState('all');
  const [subPage, setSubPage] = useState(1);
  const [subPagination, setSubPagination] = useState({ total: 0, pages: 1 });
  const [subLoading, setSubLoading] = useState(false);

  // Modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showSubDetailModal, setShowSubDetailModal] = useState(false);

  const itemsPerPage = 10;

  const PLAN_COLORS = {
    free: '#6B7280',
    pro: '#4F46E5',
    pro_plus: '#7C3AED',
    enterprise_starter: '#F59E0B',
    enterprise_scale: '#10B981',
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [txPage, txSearchTerm, txFilterPlan, activeTab]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      setSubLoading(true);
      const params = new URLSearchParams({
        page: subPage,
        limit: itemsPerPage,
      });
      if (subSearchTerm) params.append('search', subSearchTerm);
      if (subFilterPlan !== 'all') params.append('plan', subFilterPlan);
      if (subFilterStatus !== 'all') params.append('status', subFilterStatus);

      const response = await api.get(`/organizations/billing/subscriptions?${params}`);
      if (response.success) {
        setSubscriptions(response.data.subscriptions);
        setSubPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setSubLoading(false);
    }
  }, [subPage, subSearchTerm, subFilterPlan, subFilterStatus]);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
  }, [activeTab, fetchSubscriptions]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/organizations/billing/overview');
      if (response.success) {
        const data = response.data;
        setStats(data.stats);
        setSubscriptionsByPlan(data.subscriptionsByPlan);
        setSubscriptionsByStatus(data.subscriptionsByStatus || {});
        setRevenueTrends(data.revenueTrends);
        setSubscriberTrends(data.subscriberTrends || []);
        setSubscriberStats(data.subscriberStats || { total: 0, newThisWeek: 0, growthPercent: 0 });
        setTransactions(data.transactions);
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams({
        page: txPage,
        limit: itemsPerPage,
      });
      if (txSearchTerm) params.append('search', txSearchTerm);
      if (txFilterPlan !== 'all') params.append('plan', txFilterPlan);

      const response = await api.get(`/organizations/billing/transactions?${params}`);
      if (response.success) {
        setTransactions(response.data.transactions);
        setTxPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPlanBadgeVariant = (plan) => {
    switch (plan) {
      case 'pro': return 'primary';
      case 'pro_plus': return 'info';
      case 'enterprise_starter': return 'warning';
      case 'enterprise_scale': return 'success';
      default: return 'secondary';
    }
  };

  const getPlanLabel = (plan) => {
    const labels = {
      free: 'Free',
      pro: 'Pro',
      pro_plus: 'Pro Plus',
      enterprise_starter: 'Enterprise Starter',
      enterprise_scale: 'Enterprise Scale',
    };
    return labels[plan] || plan;
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'succeeded':
      case 'active': return 'success';
      case 'pending':
      case 'past_due': return 'warning';
      case 'failed':
      case 'cancelled': return 'danger';
      case 'expired': return 'secondary';
      default: return 'secondary';
    }
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  const handleViewSubscription = (sub) => {
    setSelectedSubscription(sub);
    setShowSubDetailModal(true);
  };

  const handleDownloadInvoice = (transaction) => {
    const invoiceContent = `
INVOICE
========================================
Invoice Number: ${transaction.invoiceId}
Date: ${formatDate(transaction.date)}
----------------------------------------

Organization: ${transaction.organizationName}
Plan: ${transaction.plan.toUpperCase()}

----------------------------------------
Amount: ${formatCurrency(transaction.amount)}
Currency: INR
Status: ${transaction.status.toUpperCase()}
----------------------------------------

Thank you for your subscription!

Aktivity - Training Platform
    `.trim();

    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${transaction.invoiceId}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Invoice downloaded successfully');
  };

  // Prepare donut chart data
  const planChartData = Object.entries(subscriptionsByPlan).map(([plan, count]) => ({
    name: getPlanLabel(plan),
    value: count,
    color: PLAN_COLORS[plan] || '#6B7280',
  }));
  const totalPlanSubs = planChartData.reduce((sum, d) => sum + d.value, 0);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'transactions', label: 'Transactions' },
  ];

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 space-y-5 bg-neutral-50/50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-1.5 text-neutral-500 hover:text-neutral-900 transition-colors mb-3 group"
            >
              <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Billing & Subscriptions</h1>
            <p className="text-sm text-neutral-500 mt-0.5">Monitor platform revenue, subscriptions and transactions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200 dark:border-neutral-700">
          <nav className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* ========== OVERVIEW TAB ========== */}
            {activeTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-5"
              >
                {/* Stat Cards - 3 primary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard label="Total Revenue"        value={formatCurrency(stats.totalRevenue)}            icon={DollarSign} variant="emerald" />
                  <StatCard label="Monthly Revenue"      value={formatCurrency(stats.monthlyRecurringRevenue)} icon={TrendingUp} variant="blue" />
                  <StatCard label="Active Subscriptions" value={stats.activeSubscriptions}                     icon={CreditCard} variant="violet" />
                </div>

                {/* Row 2: Revenue Trend + Subscriptions by Plan donut */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                  {/* Revenue Trend - Area Chart */}
                  <div className="lg:col-span-7 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 p-5">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100">Revenue Trend</h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Monthly revenue over time</p>
                      </div>
                    </div>
                    <div className="h-72">
                      {revenueTrends.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={revenueTrends}>
                            <defs>
                              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 11, fill: '#9CA3AF' }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fontSize: 11, fill: '#9CA3AF' }}
                              axisLine={false}
                              tickLine={false}
                              width={50}
                              tickFormatter={(value) => `₹${value >= 1000 ? (value / 1000).toFixed(0) + 'k' : value}`}
                            />
                            <Tooltip
                              contentStyle={{
                                borderRadius: '12px',
                                border: '1px solid #F3F4F6',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                padding: '10px 14px',
                              }}
                              formatter={(value) => [formatCurrency(value), 'Revenue']}
                            />
                            <Area
                              type="monotone"
                              dataKey="revenue"
                              stroke="#10B981"
                              strokeWidth={2.5}
                              fill="url(#revenueGradient)"
                              dot={false}
                              activeDot={{ r: 5, stroke: '#10B981', strokeWidth: 2, fill: '#fff' }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
                          No revenue data available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subscriptions by Plan - Donut Chart */}
                  <div className="lg:col-span-5 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 p-5">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100">Subscriptions by Plan</h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{totalPlanSubs} total subscriptions</p>
                      </div>
                    </div>
                    <div className="h-72 flex items-center">
                      {planChartData.length > 0 ? (
                        <div className="flex flex-col items-center w-full gap-4">
                          <div className="relative w-44 h-44">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={planChartData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={72}
                                  paddingAngle={3}
                                  dataKey="value"
                                  stroke="none"
                                >
                                  {planChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{
                                    borderRadius: '12px',
                                    border: '1px solid #F3F4F6',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                    padding: '8px 12px',
                                  }}
                                  formatter={(value, name) => [`${value} orgs`, name]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">{totalPlanSubs}</span>
                              <span className="text-[10px] text-neutral-400">Total</span>
                            </div>
                          </div>
                          <div className="w-full space-y-2.5 px-2">
                            {planChartData.map((item) => (
                              <div key={item.name} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{item.name}</span>
                                </div>
                                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{item.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full text-neutral-400 text-sm">
                          No subscription data
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 3: Total Subscribers - Weekly Bar Chart */}
                <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-500 flex items-center justify-center">
                        <Users size={18} />
                      </div>
                      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Total Subscriber</span>
                    </div>
                    <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 px-3 py-1.5 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
                      Weekly
                    </span>
                  </div>
                  <div className="mb-1">
                    <span className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                      {subscriberStats.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-5">
                    {subscriberStats.newThisWeek > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                        +{subscriberStats.newThisWeek} this week
                      </span>
                    )}
                    {subscriberStats.growthPercent > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                        {subscriberStats.growthPercent}% <span>&#8599;</span>
                      </span>
                    )}
                  </div>
                  <div className="h-48">
                    {subscriberTrends.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={subscriberTrends} barCategoryGap="25%">
                          <defs>
                            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#6366F1" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="#6366F1" stopOpacity={0.15} />
                            </linearGradient>
                          </defs>
                          <XAxis
                            dataKey="day"
                            tick={{ fontSize: 12, fill: '#9CA3AF', fontWeight: 500 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis hide />
                          <Tooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid #F3F4F6',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              padding: '8px 12px',
                            }}
                            formatter={(value) => [`${value} new subscribers`, 'Count']}
                            cursor={{ fill: 'transparent' }}
                          />
                          <Bar
                            dataKey="count"
                            fill="url(#barGradient)"
                            radius={[8, 8, 4, 4]}
                            maxBarSize={40}
                          >
                            <LabelList
                              dataKey="count"
                              position="top"
                              formatter={(val) => val > 0 ? val.toLocaleString() : ''}
                              style={{ fontSize: 11, fontWeight: 600, fill: '#6366F1' }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
                        No subscriber data available
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 4: Subscription Status Cards */}
                {Object.keys(subscriptionsByStatus).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: 'active', label: 'Active', icon: '●', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', dot: 'bg-emerald-500' },
                      { key: 'past_due', label: 'Past Due', icon: '●', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', dot: 'bg-amber-500' },
                      { key: 'cancelled', label: 'Cancelled', icon: '●', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', dot: 'bg-red-500' },
                      { key: 'expired', label: 'Expired', icon: '●', color: 'text-neutral-600 dark:text-neutral-400', bg: 'bg-neutral-100 dark:bg-neutral-700', dot: 'bg-neutral-400' },
                    ].map((item) => (
                      <div key={item.key} className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-2 h-2 rounded-full ${item.dot}`} />
                          <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{item.label}</span>
                        </div>
                        <p className={`text-xl sm:text-2xl font-bold ${item.color}`}>{subscriptionsByStatus[item.key] || 0}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recent Transactions Preview */}
                {transactions.length > 0 && (
                  <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 p-5">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100">Recent Transactions</h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Latest billing activity</p>
                      </div>
                      <button
                        onClick={() => setActiveTab('transactions')}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
                      >
                        View all <ArrowUpRight size={14} />
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="border-b border-neutral-100 dark:border-neutral-700">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Organization</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Plan</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Amount</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
                          {transactions.slice(0, 5).map((tx) => (
                            <tr key={tx.id} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-700/30 transition-colors">
                              <td className="py-3 px-4">
                                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{tx.organizationName}</span>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant={getPlanBadgeVariant(tx.plan)} size="sm">{getPlanLabel(tx.plan)}</Badge>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatCurrency(tx.amount)}</span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">{formatDate(tx.date)}</span>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant={getStatusBadgeVariant(tx.status)} size="sm">{tx.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* ========== SUBSCRIPTIONS TAB ========== */}
            {activeTab === 'subscriptions' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 overflow-hidden">
                  <div className="p-5 border-b border-neutral-100 dark:border-neutral-700">
                    <div className="flex flex-col md:flex-row gap-4 justify-between">
                      <div>
                        <h3 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100">All Subscriptions</h3>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Manage organization subscriptions</p>
                      </div>
                      <div className="flex gap-3 flex-wrap">
                        <div className="w-64">
                          <Input
                            placeholder="Search by organization..."
                            value={subSearchTerm}
                            onChange={(e) => {
                              setSubSearchTerm(e.target.value);
                              setSubPage(1);
                            }}
                            icon={<Search size={16} className="text-neutral-400" />}
                          />
                        </div>
                        <select
                          value={subFilterPlan}
                          onChange={(e) => { setSubFilterPlan(e.target.value); setSubPage(1); }}
                          className="px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
                        >
                          <option value="all">All Plans</option>
                          <option value="free">Free</option>
                          <option value="pro">Pro</option>
                          <option value="pro_plus">Pro Plus</option>
                          <option value="enterprise_starter">Enterprise Starter</option>
                          <option value="enterprise_scale">Enterprise Scale</option>
                        </select>
                        <select
                          value={subFilterStatus}
                          onChange={(e) => { setSubFilterStatus(e.target.value); setSubPage(1); }}
                          className="px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="past_due">Past Due</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {subLoading ? (
                    <div className="flex items-center justify-center h-48">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="border-b border-neutral-100 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/50">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Organization</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Plan</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Price</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Billing</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Period End</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Remaining</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
                          {subscriptions.length > 0 ? (
                            subscriptions.map((sub) => (
                              <tr key={sub.id} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-700/30 transition-colors">
                                <td className="py-3 px-4">
                                  <div>
                                    <button
                                      onClick={() => navigate(`/admin/organizations/${sub.organizationId}`)}
                                      className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                                    >
                                      {sub.organizationName}
                                    </button>
                                    {sub.organizationEmail && (
                                      <p className="text-xs text-neutral-400">{sub.organizationEmail}</p>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <Badge variant={getPlanBadgeVariant(sub.plan)} size="sm">
                                    {getPlanLabel(sub.plan)}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4">
                                  <Badge variant={getStatusBadgeVariant(sub.status)} size="sm">
                                    {sub.status}
                                  </Badge>
                                  {sub.cancelAtPeriodEnd && (
                                    <span className="block text-xs text-amber-600 dark:text-amber-400 mt-0.5">Cancels at period end</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                    {sub.price > 0 ? formatCurrency(sub.price) : '—'}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-sm text-neutral-600 dark:text-neutral-400 capitalize">{sub.interval || '—'}ly</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{formatDate(sub.currentPeriodEnd)}</span>
                                </td>
                                <td className="py-3 px-4">
                                  {sub.status === 'past_due' ? (
                                    <span className="text-sm font-medium text-red-600 dark:text-red-400">Overdue</span>
                                  ) : sub.daysRemaining != null && sub.daysRemaining > 0 && sub.status === 'active' ? (
                                    <span className={`text-sm font-medium ${sub.daysRemaining <= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-700 dark:text-neutral-300'}`}>
                                      {sub.daysRemaining}d
                                    </span>
                                  ) : sub.plan === 'free' ? (
                                    <span className="text-sm text-neutral-400">—</span>
                                  ) : (
                                    <span className="text-sm text-neutral-400">—</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex items-center justify-end">
                                    <button
                                      onClick={() => handleViewSubscription(sub)}
                                      className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                                      title="View Details"
                                    >
                                      <Eye size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={8} className="py-12 text-center text-neutral-500">
                                No subscriptions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {subPagination.total > 0 && (
                    <div className="border-t border-neutral-100 dark:border-neutral-700 p-4">
                      <Pagination
                        currentPage={subPage}
                        totalPages={subPagination.pages}
                        onPageChange={setSubPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={subPagination.total}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ========== TRANSACTIONS TAB ========== */}
            {activeTab === 'transactions' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-100 dark:border-neutral-700 overflow-hidden">
                  <div className="p-5 border-b border-neutral-100 dark:border-neutral-700">
                    <div className="flex flex-col md:flex-row gap-4 justify-between">
                      <div>
                        <h3 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100">All Transactions</h3>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Complete billing transaction history</p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-64">
                          <Input
                            placeholder="Search transactions..."
                            value={txSearchTerm}
                            onChange={(e) => {
                              setTxSearchTerm(e.target.value);
                              setTxPage(1);
                            }}
                            icon={<Search size={16} className="text-neutral-400" />}
                          />
                        </div>
                        <select
                          value={txFilterPlan}
                          onChange={(e) => { setTxFilterPlan(e.target.value); setTxPage(1); }}
                          className="px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
                        >
                          <option value="all">All Plans</option>
                          <option value="free">Free</option>
                          <option value="pro">Pro</option>
                          <option value="pro_plus">Pro Plus</option>
                          <option value="enterprise_starter">Enterprise Starter</option>
                          <option value="enterprise_scale">Enterprise Scale</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-neutral-100 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/50">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Invoice</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Organization</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Plan</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Amount</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
                        {transactions.length > 0 ? (
                          transactions.map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-700/30 transition-colors">
                              <td className="py-3 px-4">
                                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{transaction.invoiceId}</span>
                              </td>
                              <td className="py-3 px-4">
                                <button
                                  onClick={() => navigate(`/admin/organizations/${transaction.organizationId}`)}
                                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                                >
                                  {transaction.organizationName}
                                </button>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant={getPlanBadgeVariant(transaction.plan)} size="sm">
                                  {getPlanLabel(transaction.plan)}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                  {formatCurrency(transaction.amount)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">{formatDate(transaction.date)}</span>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant={getStatusBadgeVariant(transaction.status)} size="sm">
                                  {transaction.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => handleViewTransaction(transaction)}
                                    className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                                    title="View Details"
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDownloadInvoice(transaction)}
                                    className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                                    title="Download Invoice"
                                  >
                                    <Download size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="py-12 text-center text-neutral-500">
                              No transactions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {txPagination.total > 0 && (
                    <div className="border-t border-neutral-100 dark:border-neutral-700 p-4">
                      <Pagination
                        currentPage={txPage}
                        totalPages={txPagination.pages}
                        onPageChange={setTxPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={txPagination.total}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Transaction Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Transaction Details"
        size="md"
      >
        {selectedTransaction && (
          <div className="space-y-4">
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Invoice Number</span>
                <span className="font-semibold text-neutral-900 dark:text-neutral-100">{selectedTransaction.invoiceId}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Organization</span>
                <button
                  onClick={() => { setShowDetailModal(false); navigate(`/admin/organizations/${selectedTransaction.organizationId}`); }}
                  className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {selectedTransaction.organizationName}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Plan</span>
                <Badge variant={getPlanBadgeVariant(selectedTransaction.plan)} size="sm">
                  {getPlanLabel(selectedTransaction.plan)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Date</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-100">{formatDate(selectedTransaction.date)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Status</span>
                <Badge variant={getStatusBadgeVariant(selectedTransaction.status)} size="sm">
                  {selectedTransaction.status}
                </Badge>
              </div>
              <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Total Amount</span>
                  <span className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatCurrency(selectedTransaction.amount)}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="ghost" onClick={() => setShowDetailModal(false)} fullWidth>Close</Button>
              <Button variant="primary" onClick={() => handleDownloadInvoice(selectedTransaction)} fullWidth>Download Invoice</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Subscription Detail Modal */}
      <Modal
        isOpen={showSubDetailModal}
        onClose={() => setShowSubDetailModal(false)}
        title="Subscription Details"
        size="md"
      >
        {selectedSubscription && (
          <div className="space-y-4">
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Organization</span>
                <button
                  onClick={() => { setShowSubDetailModal(false); navigate(`/admin/organizations/${selectedSubscription.organizationId}`); }}
                  className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {selectedSubscription.organizationName}
                </button>
              </div>
              {selectedSubscription.organizationEmail && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Email</span>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{selectedSubscription.organizationEmail}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Plan</span>
                <Badge variant={getPlanBadgeVariant(selectedSubscription.plan)} size="sm">
                  {getPlanLabel(selectedSubscription.plan)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Status</span>
                <Badge variant={getStatusBadgeVariant(selectedSubscription.status)} size="sm">
                  {selectedSubscription.status}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Billing Cycle</span>
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 capitalize">{selectedSubscription.interval}ly</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Base Price</span>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">
                  {selectedSubscription.price > 0 ? formatCurrency(selectedSubscription.price) : 'Free'}
                </span>
              </div>
              {selectedSubscription.price > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">GST (18%)</span>
                    <span className="text-sm text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(Math.round(selectedSubscription.price * 0.18))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Total (incl. GST)</span>
                    <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(Math.round(selectedSubscription.price * 1.18))}
                    </span>
                  </div>
                </>
              )}
              <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Period Start</span>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{formatDate(selectedSubscription.currentPeriodStart)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Period End</span>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{formatDate(selectedSubscription.currentPeriodEnd)}</span>
                </div>
                {selectedSubscription.status === 'past_due' ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">Days Remaining</span>
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">Overdue</span>
                  </div>
                ) : selectedSubscription.daysRemaining != null && selectedSubscription.daysRemaining > 0 && selectedSubscription.status === 'active' ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">Days Remaining</span>
                    <span className={`text-sm font-medium ${selectedSubscription.daysRemaining <= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
                      {selectedSubscription.daysRemaining} days
                    </span>
                  </div>
                ) : null}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Auto Renew</span>
                  <Badge variant={selectedSubscription.autoRenew ? 'success' : 'secondary'} size="sm">
                    {selectedSubscription.autoRenew ? 'Yes' : 'No'}
                  </Badge>
                </div>
                {selectedSubscription.cancelAtPeriodEnd && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">Cancels at Period End</span>
                    <Badge variant="warning" size="sm">Yes</Badge>
                  </div>
                )}
                {selectedSubscription.cancelledAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">Cancelled At</span>
                    <span className="text-sm text-red-600 dark:text-red-400">{formatDate(selectedSubscription.cancelledAt)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-neutral-200 dark:border-neutral-700">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Created</span>
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{formatDate(selectedSubscription.createdAt)}</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="ghost" onClick={() => setShowSubDetailModal(false)} fullWidth>Close</Button>
              <Button
                variant="primary"
                onClick={() => { setShowSubDetailModal(false); navigate(`/admin/organizations/${selectedSubscription.organizationId}`); }}
                fullWidth
              >
                View Organization
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default AdminBillingPage;
